import { useNavigate, useParams } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { CheckIcon, Copy, EditIcon, XCircle } from "@/components/icons";
import {
  Badge,
  Button,
  Card,
  CardContent,
  confirm,
  Loader,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  formatQtyWithUnit,
  usesAlternateReceiveUnit,
} from "@/modules/products/domain/unit-conversion";
import { ProductFormPageHeader } from "@/modules/products/presentation/ui/ProductFormPageHeader";
import type { GoodsReceiptStatus } from "@/server/platform/db/schema/inventory";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  useConfirmGoodsReceipt,
  useDeleteGoodsReceipt,
  useDuplicateGoodsReceipt,
  useGoodsReceiptQuery,
} from "../api/queries";

const STATUS_LABELS: Record<GoodsReceiptStatus, string> = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນແລ້ວ",
  void: "ຍົກເລີກ",
};

const STATUS_CLASS: Record<GoodsReceiptStatus, string> = {
  draft: "border-border bg-muted/40 text-foreground",
  confirmed: "border-leaf/40 bg-leaf/10 text-leaf",
  void: "border-destructive/30 bg-destructive/10 text-destructive",
};

const CARRIER_LABELS: Record<string, string> = {
  hung_arun: "ຮຸ່ງອາລຸນ",
  anusith: "ອານຸສິດ",
  other: "ອື່ນໆ",
};

export function GoodsReceiptDetailPage() {
  const nav = useNavigate();
  const { id } = useParams({ from: "/app/inventory/receive/$id" });
  const receipt = useGoodsReceiptQuery(id);
  const confirmReceipt = useConfirmGoodsReceipt();
  const deleteReceipt = useDeleteGoodsReceipt();
  const duplicateReceipt = useDuplicateGoodsReceipt();
  const canWrite = useActionPermission("inventory:write");

  const data = receipt.data;

  if (receipt.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        </Main>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Header />
        <Main>
          <div className="brand-card p-8 text-center text-muted-foreground text-sm">
            ບໍ່ພົບໃບຮັບເຂົ້າ
          </div>
        </Main>
      </>
    );
  }

  const isDraft = data.status === "draft";
  const isConfirmed = data.status === "confirmed";
  const importOrderId = data.importOrderId;
  const lotByLineId = new Map(
    (data.lots ?? []).map((lot) => [lot.receiptLineId, lot]),
  );

  // purchaseSubtotal / landedSubtotal เป็น snapshot ตอน confirm — ตอน draft ยังเป็น 0
  // จึงคำนวณจาก lines เพื่อแสดงผลก่อน confirm
  const purchaseSubtotal = isConfirmed
    ? Number(data.purchaseSubtotal)
    : data.lines.reduce(
        (sum, l) => sum + Number(l.unitPurchaseCost) * l.quantity,
        0,
      );
  const extraFees = Number(data.shippingFeeTotal) + Number(data.otherFeesTotal);

  async function handleConfirm() {
    const ok = await confirm({
      title: "ຢືນຢັນຮັບເຂົ້າ?",
      description:
        "ສະຕັອກຈະຖືກບວກເຂົ້າ ແລະ ບັນທຶກຕົ້ນທຸນ (landed cost) — ບໍ່ສາມາດແກ້ໄຂລາຍການໄດ້ອີກ",
      actionText: "ຢືນຢັນ",
    });
    if (!ok) return;
    try {
      await confirmReceipt.mutateAsync(id);
      toast.success("ຢືນຢັນຮັບເຂົ້າສຳເລັດ — ສະຕັອກອັບເດດແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ຢືນຢັນລົ້ມເຫຼວ");
    }
  }

  async function handleDelete() {
    const ok = await confirm({
      title: "ລຶບໃບຮັບເຂົ້າ?",
      description: isConfirmed
        ? "ສະຕັອກ/lot ທີ່ບວກໄປຈະຖືກຫັກຄືນ (ຖ້າຍັງບໍ່ຖືກຂາຍ ຫຼື ຈອງໃນອໍເດີ draft)"
        : "ໃບຮ່າງນີ້ຈະຖືກລຶບຖາວອນ",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      const result = await deleteReceipt.mutateAsync(id);
      if ("deleted" in result && result.deleted) {
        toast.success("ລຶບໃບຮັບເຂົ້າສຳເລັດ");
        nav({ to: "/app/inventory" });
        return;
      }
      toast.success("ລຶບໃບຮັບເຂົ້າສຳເລັດ — ສະຕັອກອັບເດດແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ລຶບລົ້ມເຫຼວ");
    }
  }

  async function handleDuplicate() {
    const ok = await confirm({
      title: "ຄັດລອກໃບຮັບເຂົ້າ?",
      description:
        "ລະບົບຈະສ້າງໃບຮັບເຂົ້າ draft ໃໝ່ ພ້ອມລາຍການ/ຕົ້ນທຶນຈາກໃບນີ້ (ບໍ່ກະທົບສະຕັອກຈົນກວ່າຈະຢືນຢັນ)",
      actionText: "ຄັດລອກ",
    });
    if (!ok) return;
    try {
      const created = await duplicateReceipt.mutateAsync(id);
      toast.success(`ສ້າງ draft ໃໝ່ ${created.receiptNumber} ແລ້ວ`);
      nav({
        to: "/app/inventory/receive/$id/edit",
        params: { id: created.id },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ຄັດລອກລົ້ມເຫຼວ");
    }
  }

  return (
    <>
      <Header />
      <Main>
        <ProductFormPageHeader
          title={data.receiptNumber}
          subtitle={importOrderId ? "ໃບຮັບເຂົ້າຈາກໃບສັ່ງນຳເຂົ້າ" : "ລາຍລະອຽດໃບຮັບເຂົ້າ"}
          onBack={() => nav({ to: "/app/inventory" })}
        />

        <div className="flex flex-col gap-5">
          <Card className="brand-card">
            <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">ສະຖານະ:</span>
                <Badge variant="outline" className={STATUS_CLASS[data.status]}>
                  {STATUS_LABELS[data.status]}
                </Badge>
              </div>
              <Detail
                label="ວັນທີຮັບ"
                value={formatDateTimeLocal(data.receivedAt)}
              />
              <Detail
                label="ຂົນສົ່ງ"
                value={
                  data.carrier
                    ? (CARRIER_LABELS[data.carrier] ?? data.carrier) +
                      (data.carrierNote ? ` (${data.carrierNote})` : "")
                    : "—"
                }
              />
              <Detail label="Tracking" value={data.trackingNumber || "—"} />
              {importOrderId ? (
                <div className="flex flex-col gap-0.5">
                  <span className="text-muted-foreground text-xs">
                    ໃບສັ່ງນຳເຂົ້າ
                  </span>
                  <button
                    type="button"
                    className="text-left text-secondary text-sm underline-offset-2 hover:underline"
                    onClick={() =>
                      nav({
                        to: "/app/import-orders/$id",
                        params: { id: importOrderId },
                      })
                    }
                  >
                    {data.receiptNumber.replace(/^GR-/, "")}
                  </button>
                </div>
              ) : null}
              {data.note ? <Detail label="ໝາຍເຫດ" value={data.note} /> : null}
            </CardContent>
          </Card>

          <Card className="brand-card">
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ສິນຄ້າ</TableHead>
                    <TableHead className="text-right">ຮັບເຂົ້າ</TableHead>
                    <TableHead className="text-right">→ ສະຕັອກ</TableHead>
                    <TableHead className="text-right">ຕົ້ນທຸນ/ຮັບ</TableHead>
                    <TableHead className="text-right">ຂົນສົ່ງ</TableHead>
                    <TableHead className="text-right">landed/ຂາຍ</TableHead>
                    {isConfirmed ? (
                      <TableHead className="text-right">lot ຄົງເຫຼືອ</TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.lines.map((l) => {
                    const receiveUnit =
                      l.receiveUnitLabel?.trim() || l.stockUnitLabel;
                    const hasAlt = usesAlternateReceiveUnit(l);
                    const lot = lotByLineId.get(l.id);
                    return (
                      <TableRow key={l.id}>
                        <TableCell>
                          <div className="font-medium">{l.productName}</div>
                          <div className="text-muted-foreground text-xs">
                            {l.variantName}
                            {hasAlt ? (
                              <span>
                                {" "}
                                · 1 {l.receiveUnitLabel} = {l.conversionFactor}{" "}
                                {l.stockUnitLabel}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatQtyWithUnit(l.quantity, receiveUnit)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatQtyWithUnit(l.stockQuantity, l.stockUnitLabel)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatLak(l.unitPurchaseCost)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {isConfirmed ? formatLak(l.shippingAllocated) : "—"}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-secondary tabular-nums">
                          {isConfirmed
                            ? `${formatLak(l.unitLandedCost)}/${l.stockUnitLabel}`
                            : "—"}
                        </TableCell>
                        {isConfirmed ? (
                          <TableCell className="text-right tabular-nums">
                            {lot ? (
                              <>
                                {formatQtyWithUnit(
                                  lot.quantityRemaining,
                                  l.stockUnitLabel,
                                )}
                                {lot.quantityRemaining !==
                                lot.quantityReceived ? (
                                  <span className="block text-muted-foreground text-xs">
                                    ຈາກ {lot.quantityReceived}
                                  </span>
                                ) : null}
                              </>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Separator className="my-4" />
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ຕົ້ນທຸນສິນຄ້າລວມ</span>
                  <span className="tabular-nums">
                    {formatLak(purchaseSubtotal)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ຄ່າຂົນສົ່ງ + ຄ່າອື່ນ</span>
                  <span className="tabular-nums">{formatLak(extraFees)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>ຕົ້ນທຸນລວມ (landed)</span>
                  <span className="text-secondary tabular-nums">
                    {formatLak(
                      isConfirmed
                        ? Number(data.landedSubtotal)
                        : purchaseSubtotal + extraFees,
                    )}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {canWrite ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={handleDuplicate}
                disabled={duplicateReceipt.isPending}
              >
                <Copy data-icon="inline-start" />
                ຄັດລອກເປັນ draft
              </Button>
              {isDraft ? (
                <>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() =>
                      nav({
                        to: "/app/inventory/receive/$id/edit",
                        params: { id },
                      })
                    }
                  >
                    <EditIcon data-icon="inline-start" />
                    ແກ້ໄຂ
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full border-destructive/30 text-destructive"
                    onClick={handleDelete}
                    disabled={deleteReceipt.isPending}
                  >
                    <XCircle data-icon="inline-start" />
                    ລຶບ
                  </Button>
                  <Button
                    className="rounded-full"
                    onClick={handleConfirm}
                    disabled={confirmReceipt.isPending}
                  >
                    <CheckIcon data-icon="inline-start" />
                    ຢືນຢັນຮັບເຂົ້າ
                  </Button>
                </>
              ) : data.status !== "void" ? (
                <Button
                  variant="outline"
                  className="rounded-full border-destructive/30 text-destructive"
                  onClick={handleDelete}
                  disabled={deleteReceipt.isPending}
                >
                  <XCircle data-icon="inline-start" />
                  ລຶບ (reverse)
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </Main>
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">{label}:</span>
      <span>{value}</span>
    </div>
  );
}
